import { ImgID } from "../../image_loader/img_id.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import { AbstractDialog } from "./abstract_dialog.js";
import { DialogId } from "./dialog_id.js";
import { DialogManager } from "./dialog_manager.js";
import { GoStoreDialog } from "./go_store_dialog.js";
import { FeedbackDialog } from "./feedback_dialog.js";

export interface RateGameDialogParams
{
  onRatingSubmitted?: ( rating: number ) => void;
}

export class RateGameDialog extends AbstractDialog
{
  public static readonly dialog_id = DialogId.RATE_GAME_DIALOG;

  private stars: UIImage[] = [];
  private selected_rating = 0;
  private send_button!: UINineSliceElement;

  constructor( private params: RateGameDialogParams = {} )
  {
    const body_w = LayoutScaler.widthFromDesign( 906 );
    const body_h = LayoutScaler.heightFromDesign( 920 );

    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2;

    super( DialogManager.ctx, body_x, body_y, body_w, body_h );

    this.createElements();
  }

  private createElements(): void
  {
    this.createBackground();
    this.createTopIcon();
    this.createTitle();
    this.createStars();
    this.createButton();
    this.createCloseIcon();
  }

  private createBackground(): void
  {
    const bg = new UINineSliceElement(
      this.ctx,
      0,
      LayoutScaler.yFromDesign( 120 ),
      this.rect.getW(),
      LayoutScaler.heightFromDesign( 800 ),
      ImgID.UI_CapsuleBlue,
      "capsule_bg"
    );
    this.addChild( bg );
  }

  private createTopIcon(): void
  {
    // Keep both parts at their authored proportions: the frame is deliberately
    // much wider than the round artwork and forms the raised top of the panel.
    const frame_w = LayoutScaler.widthFromDesign( 287 );
    const frame_h = LayoutScaler.heightFromDesign( 290 );
    const frame_x = ( this.rect.getW() - frame_w ) / 2;

    const frame = new UIImage(
      this.ctx,
      frame_x,
      LayoutScaler.yFromDesign( -5 ),
      frame_w,
      frame_h,
      ImgID.UI_RateDialogGameIconFrame,
    );
    this.addChild( frame );

    const top_icon_w = LayoutScaler.widthFromDesign( 210 );
    const top_icon_h = LayoutScaler.heightFromDesign( 210 );
    const top_icon_x = ( this.rect.getW() - top_icon_w ) / 2;
    const top_icon_y = LayoutScaler.yFromDesign( 35 );

    const top_icon = new UIImage(
      this.ctx,
      top_icon_x,
      top_icon_y,
      top_icon_w,
      top_icon_h,
      ImgID.UI_RateDialogGameIcon,
    );
    this.addChild( top_icon );
  }

  private createTitle(): void
  {
    const title_w = this.rect.getW();
    const title_h = LayoutScaler.heightFromDesign( 100 );
    const title_y = LayoutScaler.yFromDesign( 230 );

    const title = new UITextElement(
      this.ctx,
      0,
      title_y,
      title_w,
      title_h,
      {
        localization_key: "RATE_GAME_DIALOG.TITLE",
        font_size: LayoutScaler.textSizeFromDesign( 85 ),
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        font_weight: 900,
        auto_wrap: false,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 14 ),
        outline_color: "#003F87",
        shadow_blur: LayoutScaler.widthFromDesign( 5 ),
        shadow_color: ColorScheme.black_transparent,
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      }
    );
    this.addChild( title );
  }

  private createStars(): void
  {
    const star_w = LayoutScaler.widthFromDesign( 150 );
    const star_h = LayoutScaler.heightFromDesign( 150 );
    const gap = LayoutScaler.widthFromDesign( 10 );
    const total_w = star_w * 5 + gap * 4;
    const star_x = ( this.rect.getW() - total_w ) / 2;
    const star_y = LayoutScaler.yFromDesign( 380 );

    for ( let i = 0; i < 5; i++ )
    {
      const x = star_x + i * ( star_w + gap );

      const star = new UIImage(
        this.ctx,
        x,
        star_y,
        star_w,
        star_h,
        ImgID.UI_RateDialogStar,
        {},
        true
      );

      star.setOnClick( () => this.setRating( i + 1 ) );

      this.stars.push( star );
      this.addChild( star );
    }
  }

  private setRating( rating: number ): void
  {
    const is_first_rating = this.selected_rating === 0;
    this.selected_rating = rating;

    for ( let i = 0; i < this.stars.length; i++ )
    {
      this.stars[i].setImageId(
        i < rating ? ImgID.UI_RateDialogStarFilled : ImgID.UI_RateDialogStar
      );
    }

    if ( is_first_rating )
      this.send_button.showElement();
  }

  public getRating(): number
  {
    return this.selected_rating;
  }

  private createButton(): void
  {
    const btn_w = LayoutScaler.widthFromDesign( 670 );
    const btn_h = LayoutScaler.heightFromDesign( 250 );
    const btn_x = ( this.rect.getW() - btn_w ) / 2;
    const btn_y = LayoutScaler.yFromDesign( 610 );

    const button = new UINineSliceElement(
      this.ctx,
      btn_x,
      btn_y,
      btn_w,
      btn_h,
      ImgID.UI_GreenFrameBtn,
      "frame_btn",
      undefined,
      undefined,
      true
    );
    this.send_button = button;
    button.hideElement( 0 );

    const text = new UITextElement(
      this.ctx,
      0,
      LayoutScaler.yFromDesign( 6 ),
      btn_w,
      btn_h,
      {
        localization_key: "RATE_GAME_DIALOG.CONFIRM",
        font_size: LayoutScaler.textSizeFromDesign( 60 ),
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        font_weight: 900,
        ...TextStyleScheme.buttonShadow(),
        outline_color: ColorScheme.more_lives_buy_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_offset_y: LayoutScaler.yFromDesign( 2 ),
      }
    );

    button.addChild( text, true );
    button.setOnClick( () => this.handleConfirm() );

    this.addChild( button );
  }

  private handleConfirm(): void
  {
    if ( this.selected_rating >= 1 )
      this.params.onRatingSubmitted?.( this.selected_rating );

    if ( this.selected_rating === 5 )
    {
      GoStoreDialog.route();
    }
    else if ( this.selected_rating >= 1 )
    {
      DialogManager.routeToDialog( new FeedbackDialog() );
    }
    else
    {
      this.close();
    }
  }

  private createCloseIcon(): void
  {
    const icon_w = LayoutScaler.widthFromDesign( 114 );
    const icon_h = LayoutScaler.heightFromDesign( 114 );
    const icon_x = this.rect.getW() - icon_w + LayoutScaler.xFromDesign( 30 );
    const icon_y = LayoutScaler.yFromDesign( 90 );

    const icon = new UIImage(
      this.ctx,
      icon_x,
      icon_y,
      icon_w,
      icon_h,
      ImgID.UI_SettingsDialogCloseBtn,
      {},
      true
    );

    icon.setOnClick( () => this.close() );

    this.addChild( icon );
  }

  public render(): void
  {
    this.startRender();
    this.finishRender();
  }

  public static open( params: RateGameDialogParams = {} ): RateGameDialog
  {
    return DialogManager.openPooled( "RateGameDialog", params, () => new RateGameDialog( params ) ) as RateGameDialog;
  }

  public override isPoolable(): boolean { return true; }

  public override onReuse( params: RateGameDialogParams ): void
  {
    this.params = params ?? {};
    this.selected_rating = 0;
    for ( const star of this.stars )
      star.setImageId( ImgID.UI_RateDialogStar );
    this.send_button.hideElement( 0 );
  }

  public name(): string
  {
    return "RateGameDialog";
  }
}
